import { type NavigationGuard } from "vue-router";
import { RouteNames } from "@shared/types/localTypes/router";
import { appService } from "@shared/services/app";
import type { LinkedSessionRequest } from "@shared/types/common";
import logger from "@shared/utils/logger";
import { sessionsService } from "@shared/services/sessions";
import { LocalStorageKeys } from "@shared/types/localTypes/storage.ts";
import { useSessionLogic } from "@shared/use/useSessionLogic.ts";
import { useLoginMessage } from "@shared/use/useLoginMessage.ts";

export const ssoLogin: NavigationGuard = async (to) => {
  const sessionId = to.query.sessionId as string;
  const userName = to.query.userName as string;
  const target = to.query.target as string;
  const subscriptionId = to.query.subscriptionId as string;

  if (!sessionId || !userName) {
    await setSsoLoginFailedMessage("Missing mandatory SSO login parameters.");
    return { name: RouteNames.Root };
  }

  const linkedSessionRequest: LinkedSessionRequest = {
    linkSessionId: sessionId,
  };
  try {
    const linkedSessionResponse =
      await sessionsService.getLinkedSession(linkedSessionRequest);
    if (!linkedSessionResponse) {
      await setSsoLoginFailedMessage("Unable to receive session ID");
      return { name: RouteNames.Root };
    }

    if (!!subscriptionId && !!target && target === "upgrade") {
      return {
        name: RouteNames.QuickUpgrade,
        params: { subscriptionId },
      };
    }

    useSessionLogic().setLastSessionAction(
      LocalStorageKeys.LAST_SUCCESSFUL_LOGIN,
    );
    return { name: RouteNames.Root };
  } catch (e) {
    const message = "Communication error while receiving session ID.";
    await setSsoLoginFailedMessage(message);
    logger.error(message, e);

    return { name: RouteNames.Root };
  }
};

export const ssoLoginFailed: NavigationGuard = async () => {
  await appService.logOut();
  await setSsoLoginFailedMessage("SSO Login failed");
  return { name: RouteNames.Root };
};

const setSsoLoginFailedMessage = async (detail: string) => {
  useLoginMessage().setLoginMessage({
    type: "error",
    description: `We're sorry but the page you requested could not be displayed. Reason : ${detail}`,
  });
};
